import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { lightTheme as lightThemeClass, darkTheme as darkThemeClass } from './theme.css';

interface ThemeContextValue {
  mode: 'light' | 'dark';
  setMode: (mode: 'light' | 'dark') => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  mode: 'light',
  setMode: () => {}
});

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  // 2025-01: ダークモード対応を一時的に無効化
  //
  // 問題: vanilla-extractでスタイル定義されたコンポーネント（AnalysisWindow、Dialog等）は
  //      ダークテーマに対応済みだが、base.cssやwelcome.css.tsなど古いCSSファイルには
  //      ハードコードされた色（#333、#f5f5f5等）が大量に残っている。
  //
  // 結果: システムがダークモードの場合、vanilla-extract部分だけ黒背景になり、
  //      文字色は暗いまま（#333）で見えなくなる。
  //
  // 対応: 全てのCSSをvanilla-extractに移行してテーマ変数を使うまで、
  //      ライトモード固定で動作させる。
  //
  // TODO: 以下のファイルをvanilla-extractに移行後、ダークモード対応を再開
  //       - src/app/styles/base.css
  //       - src/app/styles/pages/welcome.css.ts (ハードコード色の置き換え)
  const [mode, setMode] = useState<'light' | 'dark'>('light');

  // システムのダークモード設定を検知（一時的に無効化）
  // useEffect(() => {
  //   const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  //   setMode(mediaQuery.matches ? 'dark' : 'light');
  //   const handler = (e: MediaQueryListEvent) => {
  //     setMode(e.matches ? 'dark' : 'light');
  //   };
  //   mediaQuery.addEventListener('change', handler);
  //   return () => {
  //     mediaQuery.removeEventListener('change', handler);
  //   };
  // }, []);

  const currentThemeClass = mode === 'dark' ? darkThemeClass : lightThemeClass;

  // Apply vanilla-extract theme class to document root
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove(lightThemeClass, darkThemeClass);
    root.classList.add(currentThemeClass);
  }, [currentThemeClass]);

  return (
    <ThemeContext.Provider value={{ mode, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * テーマモードにアクセスするためのカスタムフック
 *
 * 使用例：
 * ```tsx
 * const { mode, setMode } = useTheme();
 *
 * return (
 *   <button onClick={() => setMode(mode === 'light' ? 'dark' : 'light')}>
 *     Toggle theme
 *   </button>
 * );
 * ```
 */
export function useTheme() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}
